/**
 * React Query client configuration
 * Centralized setup for caching, refetching, and error handling
 */

import { QueryClient } from '@tanstack/react-query';

/**
 * Default query options applied to all queries
 */
const defaultQueryOptions = {
  queries: {
    // How long until data is considered stale (5 minutes)
    staleTime: 5 * 60 * 1000,

    // How long to keep unused data in cache (10 minutes)
    gcTime: 10 * 60 * 1000,

    // Retry failed requests 1 time
    retry: 1,

    // Refetch on window focus (useful for keeping data fresh)
    refetchOnWindowFocus: true,

    // Refetch on reconnect (useful for offline scenarios)
    refetchOnReconnect: true,

    // Refetch on mount to ensure data is always up-to-date
    // This is important for localStorage-based data that can change outside React Query
    refetchOnMount: true,
  },
  mutations: {
    // Retry failed mutations 0 times (user should retry manually)
    retry: 0,
  },
};

/**
 * Create and configure the React Query client
 */
export const queryClient = new QueryClient({
  defaultOptions: defaultQueryOptions,
});

/**
 * Query key factory for type-safe, consistent query keys
 * Centralized location for all query keys used in the application
 */
export const queryKeys = {
  /**
   * Evaluation query keys
   */
  evaluations: {
    all: ['evaluations'] as const,
    detail: (id: string) => ['evaluations', id] as const,
    results: (id: string) => ['evaluations', id, 'results'] as const,
  },

  /**
   * Scorecard query keys
   */
  scorecards: {
    all: ['scorecards'] as const,
    byEvaluation: (evaluationId: string) => ['scorecards', evaluationId] as const,
    detail: (id: string) => ['scorecards', id] as const,
  },

  /**
   * Template query keys
   */
  template: {
    current: ['template'] as const,
  },
} as const;
